import React from "react";

import { STAGES, channelLabel, isSriLankanMobile, sampleText } from "../../campaignUtils";
import { Action, Alert, Card, Chip, Disclosure, Field } from "../../shared/StudioControls";
import { ContentSmsErrors } from "../contentStep/ContentStep";
import { VariantTabs } from "../contentStep/VariantTabs";
import { DeliveryHistory } from "../history/DeliveryHistory";

const DESTINATION = {
  email: { label: "Internal email address", type: "email", placeholder: "name@flash.health", inputMode: "email" },
  sms: { label: "Internal phone number", type: "tel", placeholder: "+94 77 000 0000 or 077 000 0000", inputMode: "tel" },
  push: {
    label: "Internal mobile number",
    type: "tel",
    placeholder: "0771234567 or +94771234567",
    inputMode: "tel",
    help: ["Goes to the app device registered to this number", "Customer groups are not affected"]
  }
};

const MOBILE_FORMAT_ERROR = "Enter a Sri Lankan mobile number as 0771234567 or +94771234567.";
const EMAIL_FORMAT_ERROR = "Enter a valid email address.";
const isEmail = value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

export const TestStep = ({ model, derived, handlers }) => {
  const { campaign, configuration } = derived;
  const channels = (campaign.payload.settings && campaign.payload.settings.channels) || [];
  const destination = DESTINATION[model.channel] || DESTINATION.sms;
  const variants = (model.content[STAGES.indexOf(model.stage)] || {}).variants || {};
  const variant = (variants[model.channel] || {})[model.language];
  const isPush = model.channel === "push";
  const isPhone = model.channel === "sms" || isPush;
  const formatError = model.destination.trim() && (
    (isPhone && !isSriLankanMobile(model.destination) && MOBILE_FORMAT_ERROR) ||
    (model.channel === "email" && !isEmail(model.destination) && EMAIL_FORMAT_ERROR)
  );
  const destinationError = model.testFieldError || formatError || "";

  return (
    <Card
      labelledBy="cs-step-test"
      title="Test notification"
      help={["Sends the saved version to an internal inbox, phone or device", "Customers do not receive this test"]}
      aside={<Chip>Optional</Chip>}
    >
      <ul className="cs-checklist" aria-label="Channel tests for this revision">
        {channels.map(channel => {
          const done = model.tested.includes(channel);
          return (
            <li key={channel} className={done ? "is-done" : ""}>
              <span aria-hidden="true" className="cs-checklist__glyph">{done ? "✓" : "○"}</span>
              <span className="cs-strong">{channelLabel(channel)}</span>
              <span className="cs-muted">{done ? "Test submitted" : "Not tested (optional)"}</span>
            </li>
          );
        })}
      </ul>

      {!configuration.test_enabled && (
        <Alert tone="warning" title="Test notifications are turned off">Ask the backend administrator to enable test notification sending.</Alert>
      )}

      {model.uncertain && (
        <Alert tone="warning" title="The last test outcome is uncertain">
          <ul>
            <li>Check the history below first</li>
            <li>“Retry the same test” resends request {model.uncertain.request_id} · no duplicate is created</li>
          </ul>
        </Alert>
      )}

      {model.supersededTest && (
        <Alert tone="info" title="An older test is still uncertain">
          <ul>
            <li>Test {model.supersededTest.request_id} (revision {model.supersededTest.revision}) has no confirmed outcome</li>
            <li>It can’t be retried against revision {campaign.payload.revision}</li>
            <li>A new test sends the current content</li>
          </ul>
        </Alert>
      )}

      <h3 className="cs-subheading">What to send</h3>
      <VariantTabs
        content={model.content}
        stage={model.stage}
        channel={model.channel}
        language={model.language}
        onStageChange={handlers.onStageChange}
        onChannelChange={handlers.onChannelChange}
        onLanguageChange={handlers.onLanguageChange}
      />
      {variant && (
        <div className="cs-test-preview">
          {variant.subject && <p className="cs-strong">{sampleText(variant.subject)}</p>}
          <p className="cs-pre">{sampleText(variant.body)}</p>
        </div>
      )}

      <Field
        label={destination.label}
        help={destination.help || ["Only this recipient gets the test", "Customer groups are not affected"]}
        error={destinationError}
      >
        <input
          type={destination.type}
          inputMode={destination.inputMode}
          placeholder={destination.placeholder}
          autoComplete="off"
          aria-invalid={!!destinationError}
          className={destinationError ? "is-invalid" : undefined}
          disabled={!derived.canUseSaved || !!model.uncertain || !configuration.test_enabled}
          value={model.destination}
          onChange={event => handlers.onDestinationChange(event.target.value)}
        />
      </Field>
      <div className="cs-inline-actions">
        {model.uncertain ? (
          <Action disabled={!derived.canUseSaved || model.busy} onClick={handlers.onRetryTest}>
            {model.busy ? "Retrying…" : "Retry the same test"}
          </Action>
        ) : (
          <Action disabled={!derived.canSendTest} onClick={handlers.onSendTest}>
            {model.busy ? "Sending…" : "Send test notification"}
          </Action>
        )}
      </div>

      <ContentSmsErrors errors={derived.smsErrors} />

      <Disclosure summary="Notification history" defaultOpen={!!model.uncertain}>
        <DeliveryHistory counts={model.counts} logs={model.logs} isBusy={model.busy} onLogsPage={handlers.onLogsPage} />
      </Disclosure>
    </Card>
  );
};
